import React from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';

import ChecklistOutlined from '@mui/icons-material/CheckBoxRounded';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { FormRadioBlockGroup, ButtonRow, useTrackedFormik } from '@omnigenbiodata/ui';

import PortalLayout from '../../../../../../layout/Portal';
import ROUTES from '../../../../../../core/constants/routes.constant';
import { useAppSelector, useAppDispatch } from '../../../../../../store';
import { withdrawalForward } from '../../../../../../store/participant';
import { withdrawalValuesSelector } from '../../../../../../store/participant/selectors';
import { chartColours } from '../../../../../../core/constants/colours.constants';
import PageContainer from '../../../../../../components/content/PageContainer';
import PageSection from '../../../../../../components/content/PageSection';

function WithdrawalOptionsScene() {
  const dispatch = useAppDispatch();
  const values = useAppSelector(withdrawalValuesSelector);
  const navigate = useNavigate();
  const { t } = useTranslation('portal');

  const validationSchema = yup.object().shape({
    withdrawalOption: yup.string().required(t('withdrawal2.subtitle')),
  });

  const formik = useTrackedFormik(
    {
      initialValues: {
        withdrawalOption: values?.withdrawalOption?.toString() || '',
      },
      validationSchema,
      onSubmit: (values) => {
        dispatch(withdrawalForward(values));
        navigate(ROUTES.portalWithdrawalReason);
      },
    },
    'withdrawal-options',
  );

  return (
    <PortalLayout>
      <PageContainer
        title={t('withdrawal2.title')}
        icon={
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${chartColours[5]}26`,
              color: chartColours[5],
              flexShrink: 0,
              border: 1,
              borderColor: `${chartColours[5]}55`,
            }}
          >
            <WarningAmberOutlined sx={{ fontSize: 34 }} />
          </Box>
        }
      >
        <form onSubmit={formik.handleSubmit}>
          <PageSection
            title={t('withdrawal2.subtitle')}
            colour={chartColours[0]}
            icon={<ChecklistOutlined sx={{ fontSize: 28 }} />}
          >
            <FormRadioBlockGroup
              name="withdrawalOption"
              labelCentered
              label={t('withdrawal2.para1')}
              onChange={formik.handleChange}
              value={formik.values.withdrawalOption}
              options={[
                {
                  label: t('withdrawal2.options.SharingOnly.label'),
                  value: 'SharingOnly',
                  content: t('withdrawal2.options.SharingOnly.content'),
                },
                {
                  label: t('withdrawal2.options.CollectionOnly.label'),
                  value: 'CollectionOnly',
                  content: t('withdrawal2.options.CollectionOnly.content'),
                },
                {
                  label: t('withdrawal2.options.SharingAndCollection.label'),
                  value: 'SharingAndCollection',
                  content: t('withdrawal2.options.SharingAndCollection.content'),
                },
                {
                  label: t('withdrawal2.options.Complete.label'),
                  value: 'Complete',
                  content: t('withdrawal2.options.Complete.content'),
                },
              ]}
              showOr={false}
              hideLabel={false}
              error={formik.errors.withdrawalOption}
              touched={formik.touched.withdrawalOption}
            />
          </PageSection>

          <Box
            sx={{
              '& button': {
                borderRadius: 999,
                fontWeight: 900,
                textTransform: 'none',
              },
            }}
          >
            <ButtonRow
              backLabel={t('withdrawal2.back')}
              forwardLabel={t('withdrawal2.button')}
              forwardColor="error"
              buttonSize="large"
            />
          </Box>
        </form>
      </PageContainer>
    </PortalLayout>
  );
}

export default WithdrawalOptionsScene;
